import * as Sentry from '@sentry/react';

/**
 * Initializes the error monitoring SDK (Sentry) in production environments.
 * Expects VITE_SENTRY_DSN to be set for the project; otherwise no-ops.
 */
export function initMonitoring() {
  try {
    const isProd = import.meta.env.PROD;
    const dsn: string | undefined = import.meta.env.VITE_SENTRY_DSN;
    if (!isProd || !dsn) return;

    Sentry.init({
      dsn,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: 0.1,
      environment: import.meta.env.MODE,
    });
  } catch (e) {
    // Fail-safe: never crash app due to monitoring init
    console.warn('Monitoring init failed:', e);
  }
}

/**
 * Capture an exception with optional extra context.
 */
export function captureException(error: unknown, extra?: Record<string, unknown>) {
  try {
    if (!import.meta.env.PROD) return; // Only send in production
    Sentry.captureException(error, extra ? { extra } : undefined);
  } catch (e) {
    // Keep console error paths intact during local dev
    console.warn('Failed to capture exception:', e);
  }
}

/**
 * Lightweight logging helpers. In development, write to console; in production,
 * create Sentry breadcrumbs to aid debugging without leaking sensitive data.
 */
type LogLevel = 'debug' | 'info' | 'warn' | 'error';

function breadcrumb(level: LogLevel, message: string, data?: Record<string, unknown>) {
  try {
    if (import.meta.env.PROD) {
      Sentry.addBreadcrumb({
        category: 'app.log',
        level: (level === 'warn' ? 'warning' : level) as Sentry.SeverityLevel,
        message,
        data,
      });
    } else {
      // eslint-disable-next-line no-console
      let fn: typeof console.log;
      switch (level) {
        case 'error':
          fn = console.error;
          break;
        case 'warn':
          fn = console.warn;
          break;
        case 'info':
          fn = console.info;
          break;
        default:
          fn = console.debug;
      }
      fn(`[${level}]`, message, data ?? ''); // Replaced nested ternary chain with switch (S3358)
    }
  } catch {
    // no-op
  }
}

export const monitor = {
  debug: (msg: string, data?: Record<string, unknown>) => breadcrumb('debug', msg, data),
  info: (msg: string, data?: Record<string, unknown>) => breadcrumb('info', msg, data),
  warn: (msg: string, data?: Record<string, unknown>) => breadcrumb('warn', msg, data),
  error: (msg: string, data?: Record<string, unknown>) => breadcrumb('error', msg, data),
};
